import React, { forwardRef, useEffect, useState } from "react";
import MaterialTable from "material-table";
import { ToastContainer, toast } from "react-toastify";
import {
  ArrowDownward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  DeleteOutline,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn,
} from "@material-ui/icons";
import { TextField } from "@material-ui/core";
import { Container, Button, Card, Row, Col } from "react-bootstrap";
import axios from "../Components/Axios/Axios";
// Custom Components Import
import Loader from "../Components/Spinner/Spinner";
import Sidebar from "../Components/Sidebar/MainSidebar/Sidebar";
import requests from "../Components/Requests/Requests";

// ---- Rows-per-page choices for the table ----
const PAGE_SIZE_OPTIONS = [30, 60, 90, 120];

function AllWebappData() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({
    activeUsers: 0,
    coins25: 0,
    coins375: 0,
    coins100: 0,
    weekly: 0,
    monthlyPlan: 0,
    annual: 0,
    registeredUsers: 0,
  });

  // ---- Filter state ----
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
  });

  const tableIcons = {
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => (
      <ChevronRight {...props} ref={ref} />
    )),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => (
      <ChevronLeft {...props} ref={ref} />
    )),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => (
      <ArrowDownward {...props} ref={ref} />
    )),
    ThirdStateCheck: forwardRef((props, ref) => (
      <Remove {...props} ref={ref} />
    )),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  };

  useEffect(() => {
    fetchData(filters);
  }, []);

  // ---- Fetch data, accepts filters ----
  const fetchData = async (currentFilters = filters) => {
    setLoading(true);
    try {
      const response = await axios.get(requests.webappdata, {
        params: {
          fromDate: currentFilters.fromDate,
          toDate: currentFilters.toDate,
        },
      });
      if (response.data.Success === 1) {
        setData(response.data.data);
        if (response.data.totals) {
          setTotals(response.data.totals);
        }
      } else {
        toast.warning(`Something went wrong`, {
          position: "top-right",
          autoClose: 2500,
        });
      }
    } catch (err) {
      toast.warning(err.message, {
        position: "top-right",
        autoClose: 2500,
      });
    } finally {
      setLoading(false);
    }
  };

  // ---- Filter handlers ----
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    fetchData(filters);
  };

  const clearFilters = () => {
    const emptyFilters = { fromDate: "", toDate: "" };
    setFilters(emptyFilters);
    fetchData(emptyFilters);
  };

  const statCards = [
    { label: "Active Users", value: totals.activeUsers },
    { label: "Registered Users", value: totals.registeredUsers },
    { label: "25 Coins", value: totals.coins25 },
    { label: "375 Coins", value: totals.coins375 },
    { label: "100 Coins", value: totals.coins100 },
    { label: "Weekly", value: totals.weekly },
    { label: "Monthly Plan", value: totals.monthlyPlan },
    { label: "Annual", value: totals.annual },
  ];

  return (
    <>
      <Sidebar />
      <Container className="content">
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
        />
        <Card className="card">
          <Card.Header className="heading">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              className="inside-header"
            >
              <span>WEBAPP DATA</span>
              <span style={{ fontSize: "14px", fontWeight: "normal" }}>
                Total Months: {data.length}
              </span>
            </div>
          </Card.Header>
          <Card.Body className="body">
            {/* ---- Summary Cards ---- */}
            <Row style={{ marginBottom: "15px" }}>
              {statCards.map((s, i) => (
                <Col
                  key={i}
                  xs={6}
                  sm={4}
                  md={3}
                  lg={2}
                  style={{ marginBottom: "10px" }}
                >
                  <div
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "6px",
                      padding: "10px",
                      textAlign: "center",
                      background: "#f9f9f9",
                    }}
                  >
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {s.label}
                    </div>
                    <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                      {s.value ?? 0}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>

            {/* ---- Filter Bar ---- */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                marginBottom: "15px",
                alignItems: "center",
              }}
            >
              <TextField
                label="From Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.fromDate}
                onChange={(e) => handleFilterChange("fromDate", e.target.value)}
              />

              <TextField
                label="To Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.toDate}
                onChange={(e) => handleFilterChange("toDate", e.target.value)}
              />

              <Button variant="primary" size="sm" onClick={applyFilters}>
                Apply Filters
              </Button>
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                Clear
              </Button>
            </div>

            {loading ? (
              <Loader margin={"auto"} />
            ) : (
              <MaterialTable
                data={data}
                icons={tableIcons}
                columns={[
                  {
                    title: "Month",
                    field: "month",
                    cellStyle: {
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                    },
                  },
                  {
                    title: "Active Users",
                    field: "activeUsers",
                    cellStyle: { borderRight: "1px solid #e0e0e0" },
                  },
                  {
                    title: "Registered Users",
                    field: "registeredUsers",
                    cellStyle: { borderRight: "1px solid #e0e0e0" },
                  },
                  {
                    title: "25 Coins",
                    field: "coins25",
                    cellStyle: { borderRight: "1px solid #e0e0e0" },
                  },
                  {
                    title: "375 Coins",
                    field: "coins375",
                    cellStyle: { borderRight: "1px solid #e0e0e0" },
                  },
                  {
                    title: "100 Coins",
                    field: "coins100",
                    cellStyle: { borderRight: "1px solid #e0e0e0" },
                  },
                  {
                    title: "Weekly",
                    field: "weekly",
                    cellStyle: { borderRight: "1px solid #e0e0e0" },
                  },
                  {
                    title: "Monthly Plan",
                    field: "monthlyPlan",
                    cellStyle: { borderRight: "1px solid #e0e0e0" },
                  },
                  {
                    title: "Annual",
                    field: "annual",
                    cellStyle: { borderRight: "1px solid #e0e0e0" },
                  },
                ]}
                options={{
                  pageSize: 30,
                  paginationType: "normal",
                  showTitle: false,
                  pagination: true,
                  pageSizeOptions: PAGE_SIZE_OPTIONS,
                  tableLayout: "auto",
                  search: false,
                  exportButton: true,
                  // `data` already holds every month matching the filters
                  // (the backend has no pagination for this report), so the
                  // built-in exporter just needs to use all of it instead of
                  // only the rows on the current page -- no custom fetch/CSV
                  // logic needed here like the other tables.
                  exportAllData: true,
                  rowStyle: (rowData, index) => {
                    return {
                      backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
                    };
                  },
                }}
                localization={{
                  pagination: {
                    labelRowsSelect: "rows",
                    labelDisplayedRows: "{from}-{to} of {count}",
                  },
                }}
              />
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}

export default AllWebappData;
